import React, { useRef } from 'react';

// Segmented N-digit code input. Auto-advances, supports paste (distributes across
// boxes), backspace, arrow keys, and OTP autofill (autoComplete on the first box).
const OtpInput = ({
  value = '',
  onChange,
  onComplete,
  length = 6,
  autoFocus = false,
  disabled = false,
}) => {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const commit = (arr) => {
    const next = arr.join('').slice(0, length);
    onChange(next);
    if (next.length === length && arr.every((d) => d !== '')) onComplete?.(next);
  };

  const handleChange = (i, e) => {
    const typed = e.target.value.replace(/\D/g, '');
    if (!typed) return; // deletions handled in keydown
    const arr = [...digits];
    let idx = i;
    for (const ch of typed) {
      if (idx >= length) break;
      arr[idx++] = ch;
    }
    commit(arr);
    refs.current[Math.min(idx, length - 1)]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = [...digits];
      if (arr[i]) {
        arr[i] = '';
        commit(arr);
      } else if (i > 0) {
        arr[i - 1] = '';
        commit(arr);
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!paste) return;
    const arr = Array.from({ length }, (_, k) => paste[k] || '');
    commit(arr);
    refs.current[Math.min(paste.length, length - 1)]?.focus();
  };

  return (
    <div className="flex gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={d}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className="h-12 w-11 rounded-md border border-border/70 bg-white/[0.03] text-center text-lg font-medium text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 disabled:opacity-50"
        />
      ))}
    </div>
  );
};

export default OtpInput;
